import React, { useState, useEffect } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import { updateActionsErrors } from 'redux/smartchat/details/actions'
import { setAlternativesActionsErrors } from 'redux/smartchat/alternatives/actions'
import AssignmentViewUI from './AssignmentViewUI'
import { getDataOptions } from 'components/SmartChatView/utils'

const AssignmentView = ({ renderType, label, placeholder, actionType, dataWay, currentValue, onChange, error }) => {
  const { brands, delegations, modalActionRef } = useSelector(store => ({
    brands: store.centers.brands,
    delegations: store.centers.delegations,
    modalActionRef: dataWay?.type === 'alternatives'
      ? store.smartchatAlternatives.alternativesModalActionRef.find(modalActionRef => modalActionRef.alternativesId === dataWay.alternativesId) || {}
      : store.smartchatDetails.modalActionRef
  }), shallowEqual)
  const dispatch = useDispatch()
  const [operatorsOptions, setOperatorsOptions] = useState(null)
  const [loadingOperatorsOptions, setLoadingOperatorsOptions] = useState(false)
  const [value, setValue] = useState({})
  const [resetAssignmentInnerValue, setResetAssignmentInnerValue] = useState(0)
  const [dataToExport, setDataToExport] = useState(currentValue || {})
  const [previewIntegrity, setPreviewIntegrity] = useState({})
  const assignationTypeOptions = [
    { value: 'delegation', label: I18n.t('dashboard_assign-center') },
    { value: 'operator', label: I18n.t('dashboard_assign-operator') }
  ]
  if (actionType === 'eventReassignConversation') {
    assignationTypeOptions.push({
      value: 'removeOperator',
      label: I18n.t('dashboard_unassign-operator')
    })
  }

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed && !operatorsOptions && (currentValue.operator || ['operator', 'removeOperator'].includes(currentValue.assignationType))) {
      getOperatorsOptions({ brands: currentValue.brand?.value, delegations: currentValue.delegation?.value })
    }

    return () => isSubscribed = false
  }, [currentValue.assignationType])

  // Current data
  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed) {
      if (!currentValue || !Object.keys(currentValue).length) return

      if (renderType === 'action') {
        if (modalActionRef.mode !== 'update') return

        // Actualiza los valores en redux
        onChange(currentValue)
      }

      const error = { [actionType]: {} }
      const updatedValue = { ...value }
      const updatedPreviewIntegrity = { ...previewIntegrity }

      // Assignment type
      const option = assignationTypeOptions.find(assignationType => assignationType.value === currentValue.assignationType)
      if (option) {
        if (updatedPreviewIntegrity.assignationType) updatedPreviewIntegrity.assignationType = null
        updatedValue.assignationType = option
      } else {
        // Error
        if (renderType === 'action') {
          if (updatedValue.assignationType !== null) error[actionType].assignationType = I18n.t('smartchat_tooltip_warning_notFound')
        } else {
          updatedPreviewIntegrity.assignationType = I18n.t('smartchat_tooltip_warning_notFound')
        }
      }

      // Remove all operators
      if (currentValue.removeAll) {
        updatedValue.removeAll = currentValue.removeAll
      }

      // Brand
      if (brands?.length && !currentValue.removeAll) {
        const brand = brands.find(brand => brand._id === currentValue.brand)
        if (brand) {
          if (updatedPreviewIntegrity.brand) updatedPreviewIntegrity.brand = null
          updatedValue.brand = {
            value: brand._id,
            label: brand.name,
            disabled: false,
            data: brand
          }
        } else {
          // Error
          if (renderType === 'action') {
            if (updatedValue.brand !== null) error[actionType].brand = I18n.t('smartchat_tooltip_warning_noAssignment')
          } else {
            updatedPreviewIntegrity.brand = I18n.t('smartchat_tooltip_warning_noAssignment')
          }
        }
      }

      // Delegation
      if (delegations?.length && !currentValue.removeAll) {
        const delegation = delegations.find(delegation => delegation._id === currentValue.delegation)
        if (delegation) {
          if (updatedPreviewIntegrity.delegation) updatedPreviewIntegrity.delegation = null
          updatedValue.delegation = {
            value: delegation._id,
            label: delegation.name,
            disabled: false,
            data: delegation
          }
        } else {
          // Error
          if (renderType === 'action') {
            if (updatedValue.delegation !== null) error[actionType].delegation = I18n.t('smartchat_tooltip_warning_noAssignment')
          } else {
            updatedPreviewIntegrity.delegation = I18n.t('smartchat_tooltip_warning_noAssignment')
          }
        }
      }

      // Operator
      if (operatorsOptions?.length && !currentValue.removeAll) {
        const option = operatorsOptions.find(operator => operator.value === currentValue.operator)
        if (option) {
          if (updatedPreviewIntegrity.operator) updatedPreviewIntegrity.operator = null
          updatedValue.operator = option
        } else {
          // Error
          if (renderType === 'action') {
            if (updatedValue.operator !== null) error[actionType].operator = I18n.t('smartchat_tooltip_warning_notFound')
          } else {
            updatedPreviewIntegrity.operator = I18n.t('smartchat_tooltip_warning_notFound')
          }
        }
      }

      // Update local data
      setValue(updatedValue)

      // // Error reports
      if (renderType === 'action') {
        if (Object.keys(error[actionType]).length) dataWay?.type === 'alternatives'
          ? dispatch(setAlternativesActionsErrors({ alternativesId: dataWay.alternativesId, actionsErrors: error }))
          : dispatch(updateActionsErrors(error))
      } else {
        if (Object.keys(updatedPreviewIntegrity).length) setPreviewIntegrity(updatedPreviewIntegrity)
      }
    }

    return () => isSubscribed = false
  }, [currentValue, brands, delegations, operatorsOptions])

  const getOperatorsOptions = async ({ brands = null, delegations = null }) => {
    setLoadingOperatorsOptions(true)
    try {
      const { operatorsOptions } = await getDataOptions({ requestedData: ['operatorsOptions'], params: { brands, delegations } })
      setOperatorsOptions(operatorsOptions)
    } finally {
      setLoadingOperatorsOptions(false)
    }
  }

  const onChangeInner = ({ fieldType, fieldData }) => {
    const updatedValue = { ...value }
    const updatedDataToExport = { ...dataToExport }

    // Data to export
    if (fieldType === 'assignationType') {
      if (fieldData) {
        updatedValue.assignationType = fieldData
        updatedDataToExport.assignationType = fieldData.value
      }
      // Clear later fields and data
      if (updatedValue.removeAll) {
        updatedValue.removeAll = null
        updatedDataToExport.removeAll = null
      }
      if (updatedValue.brand) {
        updatedValue.brand = null
        updatedDataToExport.brand = null
      }
      if (updatedValue.delegation) {
        updatedValue.delegation = null
        updatedDataToExport.delegation = null
      }
      if (updatedValue.operator) {
        updatedValue.operator = null
        updatedDataToExport.operator = null
      }
      setResetAssignmentInnerValue(prevState => prevState + 1)
    }
    if (fieldType === 'removeAll') {
      updatedValue.removeAll = fieldData
      updatedDataToExport.removeAll = fieldData
      // Clear later fields and data
      if (updatedValue.brand) {
        updatedValue.brand = null
        updatedDataToExport.brand = null
      }
      if (updatedValue.delegation) {
        updatedValue.delegation = null
        updatedDataToExport.delegation = null
      }
      if (updatedValue.operator) {
        updatedValue.operator = null
        updatedDataToExport.operator = null
      }
      setResetAssignmentInnerValue(prevState => prevState + 1)
    }
    if (fieldType === 'assignment') {
      if (fieldData?.brand) {
        updatedValue.brand = fieldData.brand
        updatedDataToExport.brand = fieldData.brand.value
      } else {
        if (renderType === 'action') {
          updatedValue.brand = null
          updatedDataToExport.brand = null
        }
      }
      if (fieldData?.delegation) {
        updatedValue.delegation = fieldData.delegation
        updatedDataToExport.delegation = fieldData.delegation.value
      } else {
        if (renderType === 'action') {
          updatedValue.delegation = null
          updatedDataToExport.delegation = null
        }
      }
      if (fieldData?.operator) {
        updatedValue.operator = fieldData.operator
        updatedDataToExport.operator = fieldData.operator.value
      } else {
        if (renderType === 'action') {
          updatedValue.operator = null
          updatedDataToExport.operator = null
        }
      }
    }

    // Update local data
    setValue(updatedValue)
    setDataToExport(updatedDataToExport)

    // Export the data
    onChange(updatedDataToExport)
  }

  return (
    <AssignmentViewUI
      renderType={renderType}
      label={label}
      placeholder={placeholder}
      assignationTypeOptions={assignationTypeOptions}
      operatorsOptions={operatorsOptions}
      value={value}
      resetAssignmentInnerValue={resetAssignmentInnerValue}
      onChangeInner={onChangeInner}
      getOperatorsOptions={getOperatorsOptions}
      loadingOperatorsOptions={loadingOperatorsOptions}
      error={error}
      previewIntegrity={previewIntegrity}
    />
  )
}

export default AssignmentView
